let definedSeries = {
  E6: { precision: 2, values: [1, 1.5, 2.2, 3.3, 4.7, 6.8, 10] },
  E12: { precision: 2, values: [1, 1.2, 1.5, 1.8, 2.2, 2.7, 3.3, 3.9, 4.7, 5.6, 6.8, 8.2, 10] },
  E24: {
    precision: 2,
    values: [
      1, 1.1, 1.2, 1.3, 1.5, 1.6, 1.8, 2, 2.2, 2.4, 2.7, 3, 3.3, 3.6, 3.9, 4.3, 4.7, 5.1, 5.6, 6.2, 6.8, 7.5, 8.2, 9.1,
      10,
    ],
  },
  E48: {
    precision: 3,
    values: [
      1, 1.05, 1.1, 1.15, 1.21, 1.27, 1.33, 1.4, 1.47, 1.54, 1.62, 1.69, 1.78, 1.87, 1.96, 2.05, 2.15, 2.26, 2.37, 2.49,
      2.61, 2.74, 2.87, 3.01, 3.16, 3.32, 3.48, 3.65, 3.83, 4.02, 4.22, 4.42, 4.64, 4.87, 5.11, 5.36, 5.62, 5.9, 6.19,
      6.49, 6.81, 7.15, 7.5, 7.87, 8.25, 8.66, 9.09, 9.53, 10,
    ],
  },
  E96: {
    precision: 3,
    values: [
      1, 1.02, 1.05, 1.07, 1.1, 1.13, 1.15, 1.18, 1.21, 1.24, 1.27, 1.3, 1.33, 1.37, 1.4, 1.43, 1.47, 1.5, 1.54, 1.58,
      1.62, 1.65, 1.69, 1.74, 1.78, 1.82, 1.87, 1.91, 1.96, 2, 2.05, 2.1, 2.15, 2.21, 2.26, 2.32, 2.37, 2.43, 2.49,
      2.55, 2.61, 2.67, 2.74, 2.8, 2.87, 2.94, 3.01, 3.09, 3.16, 3.24, 3.32, 3.4, 3.48, 3.57, 3.65, 3.74, 3.83, 3.92,
      4.02, 4.12, 4.22, 4.32, 4.42, 4.53, 4.64, 4.75, 4.87, 4.99, 5.11, 5.23, 5.36, 5.49, 5.62, 5.76, 5.9, 6.04, 6.19,
      6.34, 6.49, 6.65, 6.81, 6.98, 7.15, 7.32, 7.5, 7.68, 7.87, 8.06, 8.25, 8.45, 8.66, 8.87, 9.09, 9.31, 9.53, 9.76,
      10,
    ],
  },
};

/**
 * getSeriesValuesBetween - Get the values in the specified series between the two specified values
 *
 * @param {number} min - the minimum value of the range
 * @param {number} max - the maximum value of the range
 * @param {string} series - which series to use
 * @returns {number[] | undefined } - the array of values in the specified series between the two limits supplied. Undefined if there are parameter errors.
 */
export function getSeriesValuesBetween(
  min, //: number,
  max, //: number,
  series // : "E6" | "E12" | "E24" | "E96"
) {
  // : number[] | undefined
  if (!definedSeries[series]) {
    return;
  }
  let minS = normalizeToPreferredSeries(min, series, { forceRoundDirection: 'up' });
  let maxS = normalizeToPreferredSeries(max, series, { forceRoundDirection: 'down' });

  return generateSeries(minS, maxS, definedSeries[series]);
}

/**
 * normalizeToPreferredSeries - normalize a value to the nearest value in a defined series
 *
 * @param {number} idealValue - the raw value to be normalized
 * @param {string} series - the series to which to normalize it
 * @param {object} options - optional features -
 *          forceRoundDirection: "up", "down", "closest"
 * @returns {number | undefined} - the normalized value. Undefined if there are parameter errors.
 */

export function normalizeToPreferredSeries(
  idealValue, //: number,
  series, //: "E6" | "E12" | "E24" | "E48" | "E96",
  options // { forceRoundDirection: "up" |"down" | "closest" },
) {
  // : number | undefined;
  if (!definedSeries[series]) return;

  let preferredSeries = definedSeries[series].values;
  let forceRoundDirection = options
    ? options.forceRoundDirection
      ? options.forceRoundDirection
      : 'closest'
    : 'closest';

  let scientificNotation = idealValue.toExponential().toString();
  var indexOfE = scientificNotation.indexOf('e');
  let exponent = scientificNotation.slice(indexOfE, scientificNotation.length);
  let desiredValue = parseFloat(scientificNotation.slice(0, indexOfE));

  let nominalValue = desiredValue;
  for (var i = 0; i < preferredSeries.length; i++) {
    if (preferredSeries[i] <= desiredValue && desiredValue < preferredSeries[i + 1]) {
      var distanceBelow = desiredValue / preferredSeries[i];
      var distanceAbove = preferredSeries[i + 1] / desiredValue;

      if (forceRoundDirection === 'down' && distanceAbove > 1) {
        nominalValue = preferredSeries[i];
      } else if (forceRoundDirection === 'up' && distanceBelow > 1) {
        nominalValue = preferredSeries[i + 1];
      }
      //round to closest
      else {
        if (distanceBelow < distanceAbove) {
          nominalValue = preferredSeries[i];
        } else if (distanceAbove < distanceBelow) {
          nominalValue = preferredSeries[i + 1];
        }
      }

      break;
    }
  }
  nominalValue = nominalValue.toString() + exponent;
  return parseFloat(nominalValue);
}

function generateSeries(min, max, series) {
  let [minIndex, minPower] = placeInSeries(min, series);
  let [maxIndex, maxPower] = placeInSeries(max, series);

  let index = minIndex;
  let power = minPower;
  let multiplier = 10 ** power;
  let values = [];
  while (power < maxPower || index <= maxIndex) {
    values.push(series.values[index] * multiplier);
    index++;
    if ((index >= series.values.length - 1 && power < maxPower) || index >= series.values.length) {
      index = 0;
      power++;
      multiplier = 10 ** power;
    }
  }
  return values;
}

let placeInSeries = function (value, series) {
  let log = Math.log10(value);
  let power = Math.floor(log);
  let fraction = log - power;
  let fractionalValue = 10 ** fraction;
  let index = -1;
  for (let i = 0; i < series.values.length; i++) {
    if (Math.abs(series.values[i] - fractionalValue) < 0.01) {
      index = i;
      break;
    }
  }
  return [index, power];
};
